import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  NOTIFICATION_TYPE,
  SUBSCRIPTION_STATUS_TYPE,
} from '../../../../../constants/benchat';
import { notify as pnotifyNotify } from '../../../../../service/pnotifyService';
import { UseBenchatSubscriptionsPropTypes } from '../../../utils/propTypes';
import {
  FacebookSubscriptionButton,
  FacebookSubscriptionDialog,
} from '../../../../../component/FacebookSubscription';
import benchatService from '../../../../../service/benchatService';
import trackerService from '../../../../../service/trackerService';
import {
  EVENT_NAME_FOR_SUBSCRIPTION,
  EVENT_CATEGORY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION,
} from '../../../../../shop/constants/tracker';

function FacebookSubscription({ platformChannelId, benchat }) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusFlag, setFocusFlag] = useState(false);

  const onOpen = useCallback(() => {
    if (benchat.hasSubscribed) return;
    setIsOpen(true);

    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_BUTTON_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_TYPE]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.PROMOTION_NOTIFICATION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_PATH]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.SIGN_UP_PAGE,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.FACEBOOK,
      },
    });
  }, [benchat.hasSubscribed]);
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onConfirm = useCallback(() => {
    const url = benchatService.getMDotMeLink(
      platformChannelId,
      `${benchat.userRef.current}-${NOTIFICATION_TYPE.MARKETING}`,
    );
    window.open(url);

    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_POPUP_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.FACEBOOK,
      },
    });

    setFocusFlag(true);
    setIsOpen(false);
  }, [platformChannelId, benchat.userRef]);

  useEffect(() => {
    if (!focusFlag) return;

    const onWindowFocus = async () => {
      setIsLoading(true);
      benchat.startPolling();
      setFocusFlag(false);
    };
    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [focusFlag, benchat, t]);

  useEffect(() => {
    switch (benchat.subscriptionStatus) {
      // since we check when user focus on window, which doesn't means user complete the subscription flow
      // so we don't show notify when NO_RECORD
      case SUBSCRIPTION_STATUS_TYPE.NO_RECORD:
      case SUBSCRIPTION_STATUS_TYPE.INITIAL:
        setIsLoading(false);
        break;
      case SUBSCRIPTION_STATUS_TYPE.LOADING:
        setIsLoading(true);
        break;
      case SUBSCRIPTION_STATUS_TYPE.SUCCESS:
        setIsLoading(false);
        pnotifyNotify(t('Subscription - connection notification - success'));
        break;
      case SUBSCRIPTION_STATUS_TYPE.UNKNOWN_ERROR:
        setIsLoading(false);
        pnotifyNotify(
          t('Subscription - FACEBOOK - connection notification - failure'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      default:
        break;
    }
  }, [benchat.subscriptionStatus, t]);

  return (
    <>
      <FacebookSubscriptionButton
        className="facebook-subscription-button_signup"
        isLoading={isLoading}
        isSubscribed={benchat.hasSubscribed}
        onClick={onOpen}
        e2eId="signup-subscription_button--facebook"
      >
        {benchat.hasSubscribed
          ? t('Subscription - Facebook - subscribed')
          : t('Subscription - Facebook - subscribe')}
      </FacebookSubscriptionButton>
      <FacebookSubscriptionDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    </>
  );
}

FacebookSubscription.propTypes = {
  platformChannelId: PropTypes.string.isRequired,
  benchat: UseBenchatSubscriptionsPropTypes,
};

export default FacebookSubscription;
